import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { Template } from '../../_types';

export const getCorrectNavigationUrl = (template: Template) => {
  if (template.organizationId !== null) {
    return '/admin/performance-reporting/templates';
  }

  // Global/jurisdiction templates (organizationId === null): Check if this is a child template
  const isChildTemplate = !!template.parentTemplateId;
  if (isChildTemplate && template.parentTemplateId) {
    // Navigate to parent template's Child Templates tab
    return `/admin/performance-reporting/templates/details/${template.parentTemplateId}#tab=childTemplates`;
  } else {
    // Navigate to templates list for parent templates
    return '/admin/performance-reporting/templates';
  }
};

const navigateAfterTemplateAction = (
  navigate: ReturnType<typeof useNavigate>,
  template: Template,
  beforeNavigateActionsFn?: () => void
) => {
  if (beforeNavigateActionsFn) {
    beforeNavigateActionsFn();
  }

  const url = getCorrectNavigationUrl(template);
  navigate(url);
};

export const useNavigationHandlers = (
  template: Template,
  resetEditingState?: () => void,
  enforceAutoSave?: (template: Template, note: string) => Promise<Template>,
  templateEditable?: Template,
  setIsTemplateDisabled?: (value: boolean) => void
) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Navigation
  const handleBackToTemplateList = useCallback(() => {
    if (resetEditingState) {
      resetEditingState();
    }
    // Invalidate queries to refresh the list
    queryClient.invalidateQueries(['templates']);
    if (template.id) {
      queryClient.invalidateQueries(['template', template.id]);
      if (template.parentTemplateId) {
        queryClient.invalidateQueries(['childTemplates', template.parentTemplateId]);
      }
    }
    navigate('/admin/performance-reporting/templates');
  }, [navigate, resetEditingState, queryClient, template.id, template.parentTemplateId]);

  const handleEndEditing = useCallback(async () => {
    // Fallback to old behavior if new params not provided
    if (!enforceAutoSave || !templateEditable || !setIsTemplateDisabled) {
      queryClient.invalidateQueries(['templates']);
      if (template.id) {
        queryClient.invalidateQueries(['template', template.id]);
        if (template.parentTemplateId) {
          queryClient.invalidateQueries(['childTemplates', template.parentTemplateId]);
        }
      }
      navigateAfterTemplateAction(navigate, template, resetEditingState);
      return;
    }

    setIsTemplateDisabled(true);
    try {
      // Enforce save before ending
      await enforceAutoSave(templateEditable, 'Pre-navigation save');

      // Invalidate queries before navigating
      queryClient.invalidateQueries(['templates']);
      if (template.id) {
        queryClient.invalidateQueries(['template', template.id]);
        if (template.parentTemplateId) {
          queryClient.invalidateQueries(['childTemplates', template.parentTemplateId]);
        }
      }
      navigateAfterTemplateAction(navigate, template, resetEditingState);
    } catch (error) {
      console.error('Error saving before navigation:', error);
      setIsTemplateDisabled(false);
    }
  }, [navigate, resetEditingState, template, queryClient, enforceAutoSave, templateEditable, setIsTemplateDisabled]);

  const handlePublish = useCallback(
    (updatedTemplate?: Template) => {
      const templateToUse = updatedTemplate || template;
      // Invalidate queries before navigating
      queryClient.invalidateQueries(['templates']);
      if (templateToUse.id) {
        queryClient.invalidateQueries(['template', templateToUse.id]);
        if (templateToUse.parentTemplateId) {
          queryClient.invalidateQueries(['childTemplates', templateToUse.parentTemplateId]);
        }
      }
      navigateAfterTemplateAction(navigate, templateToUse);
    },
    [navigate, template, queryClient]
  );

  return {
    handleBackToTemplateList,
    handleEndEditing,
    handlePublish,
  };
};
